// apps/web/src/components/astral/AstralTemplatesModal.tsx
import { X, LayoutTemplate, ArrowRight } from "lucide-react";
import { ASTRAL_TEMPLATES, type AstralTemplate } from "../../data/astralTemplates";

interface AstralTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: AstralTemplate) => void;
}

export function AstralTemplatesModal({
  isOpen,
  onClose,
  onSelectTemplate,
}: AstralTemplatesModalProps) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(12px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "840px",
          maxHeight: "85vh",
          backgroundColor: "#111622",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: "16px",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.8)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                backgroundColor: "rgba(56, 139, 253, 0.15)",
                color: "var(--apple-blue)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LayoutTemplate size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "var(--ink)" }}>
                Modelos de Mapas Mentais & Organogramas
              </h2>
              <span style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
                Selecione um blueprint pronto para começar com processos validados da agência
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: "30px",
              height: "30px",
              minWidth: "30px",
              borderRadius: "50%",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              background: "rgba(255, 255, 255, 0.05)",
              color: "var(--ink-muted)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
            title="Fechar"
          >
            <X size={15} />
          </button>
        </div>

        {/* Templates Grid */}
        <div
          style={{
            padding: "24px",
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: "16px",
          }}
        >
          {ASTRAL_TEMPLATES.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => onSelectTemplate(tpl)}
              style={{
                padding: "18px",
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "14px",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--apple-blue)";
                e.currentTarget.style.backgroundColor = "rgba(56, 139, 253, 0.05)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.03)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: "6px",
                      backgroundColor: "rgba(56, 139, 253, 0.15)",
                      color: "var(--apple-blue)",
                    }}
                  >
                    {tpl.badge}
                  </span>
                  <span style={{ fontSize: "11px", color: "var(--ink-muted)" }}>
                    {Object.keys(tpl.data.nodes).length} tópicos
                  </span>
                </div>

                <strong style={{ fontSize: "14.5px", color: "var(--ink)", display: "block", marginBottom: "6px" }}>
                  {tpl.title}
                </strong>
                <p style={{ fontSize: "12.5px", color: "var(--ink-muted)", margin: 0, lineHeight: 1.4 }}>
                  {tpl.description}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: "10px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--apple-blue)",
                }}
              >
                <span>Usar este modelo</span>
                <ArrowRight size={14} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
